<template>
	<v-form class="employee-form mb-4">
		<v-layout row>
			<v-flex shrink>
				<v-navigation-drawer permanent width="200">
					<v-list dense class="pt-0">
						<v-list-tile v-if="shouldDisplayForm('basicInfo')" @click.prevent.stop="navigateToAnchorTag('basicInfo')" :class="{ 'has-error': basicInfoFormHasError }">
							<v-list-tile-content>
								<v-list-tile-title>Basic Info</v-list-tile-title>
							</v-list-tile-content>
							<v-list-tile-action>
								<v-tooltip v-if="basicInfoFormHasError" bottom>
									<template v-slot:activator="{ on }">
										<v-icon color="error" v-on="on">error</v-icon>
									</template>
									<span>This section has errors</span>
								</v-tooltip>
							</v-list-tile-action>
						</v-list-tile>
						<v-list-tile v-if="shouldDisplayForm('work')" @click.prevent.stop="navigateToAnchorTag('work')" :class="{ 'has-error': workFormHasError }">
							<v-list-tile-content>
								<v-list-tile-title>Work</v-list-tile-title>
							</v-list-tile-content>
							<v-list-tile-action>
								<v-tooltip v-if="workFormHasError" bottom>
									<template v-slot:activator="{ on }">
										<v-icon color="error" v-on="on">error</v-icon>
									</template>
									<span>This section has errors</span>
								</v-tooltip>
							</v-list-tile-action>
						</v-list-tile>
						<v-list-tile v-if="shouldDisplayForm('personal')" @click.prevent.stop="navigateToAnchorTag('personal')">
							<v-list-tile-content>
								<v-list-tile-title>Personal</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
						<v-list-tile v-if="shouldDisplayForm('address')" @click.prevent.stop="navigateToAnchorTag('address')" :class="{ 'has-error': addressFormHasError }">
							<v-list-tile-content>
								<v-list-tile-title>Address</v-list-tile-title>
							</v-list-tile-content>
							<v-list-tile-action>
								<v-tooltip v-if="addressFormHasError" bottom>
									<template v-slot:activator="{ on }">
										<v-icon color="error" v-on="on">error</v-icon>
									</template>
									<span>This section has errors</span>
								</v-tooltip>
							</v-list-tile-action>
						</v-list-tile>
						<v-list-tile v-if="shouldDisplayForm('emergency')" @click.prevent.stop="navigateToAnchorTag('emergency')">
							<v-list-tile-content>
								<v-list-tile-title>Emergency</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
						<v-list-tile v-if="shouldDisplayForm('tags')" @click.prevent.stop="navigateToAnchorTag('tags')">
							<v-list-tile-content>
								<v-list-tile-title>Tags</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
						<v-divider></v-divider>
						<v-list-tile v-if="shouldDisplayForm('hrPolicies')" @click.prevent.stop="navigateToAnchorTag('hrPolicies')">
							<v-list-tile-content>
								<v-list-tile-title>HR Policies</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
						<v-list-tile v-if="shouldDisplayForm('logins')" @click.prevent.stop="navigateToAnchorTag('logins')">
							<v-list-tile-content>
								<v-list-tile-title>Logins</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
						<v-list-tile v-if="loggedInEmployee.isAdmin && shouldDisplayForm('otherDetails')" @click.prevent.stop="navigateToAnchorTag('otherDetails')">
							<v-list-tile-content>
								<v-list-tile-title>Other</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
					</v-list>
				</v-navigation-drawer>
			</v-flex>
			<v-flex class="ml-4 scrollable-container" ref="scrollContainer">
				<employee-form-card-container
					v-if="shouldDisplayForm('basicInfo')"
					ref="basicInfoForm"
					form="BasicInfoForm"
					:value="form.basicInfo"
					@input="onBasicInfoUpdated"
					@errorStateChanged="onBasicInfoErrorStateChanged"
					:is-saving="isBasicInfoSaving"
					:can-edit="canEdit"
					:default-state="getDefaultState('basicInfo')"
					title="Basic Info"
					class="mb-5"
					id="basicInfo"
					:submit-btn-text="formContainerSubmitBtnText"
				></employee-form-card-container>
				<employee-form-card-container
					v-if="shouldDisplayForm('work')"
					ref="workDetailsForm"
					form="WorkDetailsForm"
					:value="form.work"
					@input="onWorkUpdated"
					@errorStateChanged="onWorkErrorStateChanged"
					:can-edit="canEdit"
					:default-state="getDefaultState('work')"
					:is-saving="isWorkSaving"
					title="Work"
					class="mb-5"
					id="work"
					:submit-btn-text="formContainerSubmitBtnText"
				></employee-form-card-container>
				<employee-form-card-container
					v-if="shouldDisplayForm('personal')"
					ref="personalDetailsForm"
					form="PersonalDetailsForm"
					:value="form.personalDetails"
					@input="onPersonalDetailsUpdated"
					@errorStateChanged="onPersonalErrorStateChanged"
					:can-edit="canEdit"
					:default-state="getDefaultState('personal')"
					:is-saving="isPersonalSaving"
					title="Personal"
					class="mb-5"
					id="personal"
					:submit-btn-text="formContainerSubmitBtnText"
				></employee-form-card-container>
				<employee-residential-address-history
					v-if="persistChanges && shouldDisplayForm('address')"
					v-model="form.residentialAddresses"
					:employee-id="employeeId"
					:can-edit="canEdit"
					class="mb-5"
					id="address"
				></employee-residential-address-history>
				<employee-form-card-container
					v-else-if="shouldDisplayForm('address')"
					ref="residentialAddressForm"
					form="ResidentialAddressForm"
					:value="form.residentialAddress"
					@input="onResidentialAddressUpdated"
					@errorStateChanged="onResidentialAddressErrorStateChanged"
					:can-edit="canEdit"
					:default-state="getDefaultState('address')"
					:form-props="{ allowEmpty: true }"
					title="Address"
					class="mb-5"
					id="address"
					:submit-btn-text="formContainerSubmitBtnText"
				></employee-form-card-container>
				<employee-form-card-container
					v-if="shouldDisplayForm('emergency')"
					ref="emergencyContactForm"
					form="EmergencyContactForm"
					:value="form.emergency"
					@input="onEmergencyUpdated"
					@errorStateChanged="onEmergencyErrorStateChanged"
					:can-edit="canEdit"
					:default-state="getDefaultState('emergency')"
					:is-saving="isEmergencySaving"
					title="Emergency"
					class="mb-5"
					id="emergency"
					:submit-btn-text="formContainerSubmitBtnText"
				></employee-form-card-container>
				<employee-form-card-container
					v-if="shouldDisplayForm('tags')"
					ref="tagsForm"
					form="TagsForm"
					:value="form.tags"
					@input="onTagsUpdated"
					:can-edit="canEdit"
					:default-state="getDefaultState('tags')"
					:is-saving="isTagsSaving"
					title="Tags"
					class="mb-5"
					id="tags"
					:submit-btn-text="formContainerSubmitBtnText"
				></employee-form-card-container>
				<employee-form-card-container
					v-if="shouldDisplayForm('hrPolicies')"
					ref="hrPoliciesForm"
					form="HrPoliciesForm"
					:value="form.hrPolicies"
					@input="onHrPoliciesUpdated"
					:is-saving="isHrPoliciesSaving"
					:can-edit="canEdit"
					:default-state="getDefaultState('hrPolicies')"
					:employeeId="employeeId"
					title="HR Policies"
					class="mb-5"
					id="hrPolicies"
					:submit-btn-text="formContainerSubmitBtnText"
				></employee-form-card-container>
				<flat-border-card v-if="shouldDisplayForm('logins')" title="Logins" ref="loginsForm" id="logins" class="mb-5">
					<employee-user-account :employeeId="employeeId" :employeeEmail="form.basicInfo.email" @onUserAccountUpdated="onUserAccountUpdated" />
				</flat-border-card>
				<employee-form-card-container
					v-if="shouldDisplayForm('otherDetails') && loggedInEmployee.isAdmin"
					ref="otherDetailsForm"
					form="OtherDetailsForm"
					:value="form.otherDetails"
					:can-edit="canEdit"
					:default-state="getDefaultState('otherDetails')"
					@input="onOtherDetailsUpdated"
					:is-saving="isOtherSaving"
					title="Other"
					class="mb-5"
					id="otherDetails"
					:submit-btn-text="formContainerSubmitBtnText"
				></employee-form-card-container>
			</v-flex>
		</v-layout>
	</v-form>
</template>

<script>
	import EmployeeFormCardContainer from '@/components/employee/forms/EmployeeFormCardContainer';
	import EmployeeUserAccount from '@/components/employee/EmployeeUserAccount';
	import FlatBorderCard from '@/components/common/FlatBorderCard';
	import EmployeeResidentialAddressHistory from '@/components/employee/EmployeeResidentialAddressHistory';

	import { mapGetters } from 'vuex';
	import toast from '@/services/toasts/index.js';

	export default {
		components: {
			EmployeeFormCardContainer,
			FlatBorderCard,
			EmployeeUserAccount,
			EmployeeResidentialAddressHistory
		},
		props: {
			persistChanges: {
				type: Boolean,
				default: false
			},
			employeeId: {
				type: String,
				required: false
			},
			value: {
				type: Object,
				default: () => ({
					basicInfo: {
						firstName: null,
						lastName: null,
						middleName: null
					},
					work: {
						department: null,
						email: null,
						phone: null
					},
					personalDetails: {
						prefix: null,
						dateOfBirth: {
							date: null,
							menu: false
						},
						nationality: null
					},
					residentialAddress: {
						effectiveDate: null,
						addressLine1: null,
						addressLine2: null,
						suburb: null,
						state: null,
						postcode: null,
						country: 'Australia'
					},
					residentialAddresses: [],
					emergency: {
						firstName: null,
						lastName: null,
						email: null,
						phoneNo: null,
						relation: null
					},
					otherDetails: {
						hideFromEmployeeDirectory: false
					},
					hrPolicies: [],
					tags: [],
					login: {
						userId: null
					}
				})
			},
			forms: {
				type: Array,
				required: true
			}
		},
		data() {
			return {
				form: this.value,
				basicInfoFormHasError: false,
				workFormHasError: false,
				emergencyFormHasError: false,
				personalFormHasError: false,
				addressFormHasError: false
			};
		},
		mounted() {
			this.updateHashBasedOnScroll = this.debounce(() => {
				const sections = this.$refs.scrollContainer.querySelectorAll('div.form[id]');
				let scrollPosition = this.$refs.scrollContainer.scrollTop;

				sections.forEach(section => {
					if (section.offsetTop <= scrollPosition && section.offsetTop + section.offsetHeight > scrollPosition) {
						window.history.replaceState(null, null, '#' + section.id);
					}
				});
			}, 10);

			this.$refs.scrollContainer.addEventListener('scroll', this.updateHashBasedOnScroll);
		},
		beforeDestroy() {
			this.$refs.scrollContainer.removeEventListener('scroll', this.updateHashBasedOnScroll);
		},
		watch: {
			form: {
				handler(newValue) {
					this.$emit('input', newValue);
				},
				deep: true
			},
			value(newValue) {
				this.form = newValue;
			},
			deep: true
		},
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				isBasicInfoSaving: 'employees/isBasicInfoSaving',
				isWorkSaving: 'employees/isWorkSaving',
				isPersonalSaving: 'employees/isPersonalSaving',
				isEmergencySaving: 'employees/isEmergencySaving',
				isTagsSaving: 'employees/isTagsSaving',
				isHrPoliciesSaving: 'employees/isHrPoliciesSaving',
				isOtherSaving: 'employees/isOtherSaving'
			}),
			formContainerSubmitBtnText() {
				return this.persistChanges ? 'Save' : 'Done';
			},
			canEdit() {
				const isUnderling = this.loggedInEmployee.underlings != null && this.loggedInEmployee.underlings.includes(this.employeeId);
				return this.loggedInEmployee.isAdmin || isUnderling;
			}
		},
		methods: {
			shouldDisplayForm(form) {
				return this.forms.find(f => f.name === form);
			},
			getDefaultState(form) {
				const foundForm = this.forms.find(f => f.name === form);
				if (foundForm && foundForm.hasOwnProperty('defaultState')) {
					return foundForm.defaultState;
				}

				return 'view';
			},
			debounce(func, wait, immediate) {
				let timeout;
				return function() {
					let context = this,
						args = arguments;
					let later = function() {
						timeout = null;
						if (!immediate) func.apply(context, args);
					};
					let callNow = immediate && !timeout;
					clearTimeout(timeout);
					timeout = setTimeout(later, wait);
					if (callNow) func.apply(context, args);
				};
			},
			navigateToAnchorTag(anchorTag) {
				document.getElementById(`${anchorTag}`).scrollIntoView({
					behavior: 'smooth'
				});
			},
			validateAndSubmit() {
				if (!this.$refs.emergencyContactForm || !this.$refs.basicInfoForm || !this.$refs.workDetailsForm || !this.$refs.personalDetailsForm || !this.$refs.tagsForm || !this.$refs.hrPoliciesForm || !this.$refs.otherDetailsForm) return false;

				const emergencyContactFormIsValid = this.$refs.emergencyContactForm.submit();
				if (!emergencyContactFormIsValid) this.$refs.emergencyContactForm.toggleEdit(true);

				const basicInfoFormIsValid = this.$refs.basicInfoForm.submit();
				if (!basicInfoFormIsValid) this.$refs.basicInfoForm.toggleEdit(true);

				const workDetailsFormIsValid = this.$refs.workDetailsForm.submit();
				if (!workDetailsFormIsValid) this.$refs.workDetailsForm.toggleEdit(true);

				const personalDetailsFormIsValid = this.$refs.personalDetailsForm.submit();
				if (!personalDetailsFormIsValid) this.$refs.personalDetailsForm.toggleEdit(true);

				const residentialAddressFormIsValid = !this.$refs.residentialAddressForm || this.$refs.residentialAddressForm.submit();
				if (!residentialAddressFormIsValid) this.$refs.residentialAddressForm.toggleEdit(true);

				const tagsFormIsValid = this.$refs.tagsForm.submit();
				if (!tagsFormIsValid) this.$refs.tagsForm.toggleEdit(true);

				const hrPoliciesFormIsValid = this.$refs.hrPoliciesForm.submit();
				if (!hrPoliciesFormIsValid) this.$refs.hrPoliciesForm.toggleEdit(true);

				const otherDetailsFormIsValid = this.$refs.otherDetailsForm.submit();
				if (!otherDetailsFormIsValid) this.$refs.otherDetailsForm.toggleEdit(true);

				const isValid = emergencyContactFormIsValid && basicInfoFormIsValid && workDetailsFormIsValid && personalDetailsFormIsValid && residentialAddressFormIsValid && tagsFormIsValid && hrPoliciesFormIsValid && otherDetailsFormIsValid;

				return isValid;
			},
			async onBasicInfoUpdated(newVal) {
				if (this.persistChanges) {
					const result = await this.$store.dispatch('employees/updateBasicInfo', {
						...newVal,
						employeeId: this.employeeId
					});

					if (result.isSuccess) {
						toast.success('Employee details updated');
						this.$refs.basicInfoForm.toggleEdit(false);
						this.form.basicInfo = newVal;
					}
				} else {
					this.$refs.basicInfoForm.toggleEdit(false);
					this.form.basicInfo = newVal;
				}
			},
			onBasicInfoErrorStateChanged(newVal) {
				this.basicInfoFormHasError = newVal;
			},
			async onWorkUpdated(newVal) {
				if (this.persistChanges) {
					const result = await this.$store.dispatch('employees/updateWork', {
						employeeId: this.employeeId,
						departmentId: newVal.department.id,
						phone: newVal.phone,
						email: newVal.email
					});

					if (result.isSuccess) {
						toast.success('Employee details updated');
						this.$refs.workDetailsForm.toggleEdit(false);
						this.form.work = newVal;
					}
				} else {
					this.$refs.workDetailsForm.toggleEdit(false);
					this.form.work = newVal;
				}
			},
			onWorkErrorStateChanged(newVal) {
				this.workFormHasError = newVal;
			},
			async onPersonalDetailsUpdated(newVal) {
				if (this.persistChanges) {
					const result = await this.$store.dispatch('employees/updatePersonal', {
						employeeId: this.employeeId,
						prefix: newVal.prefix,
						dateOfBirth: newVal.dateOfBirth.date,
						nationality: newVal.nationality,
						email: newVal.email,
						phone: newVal.phone,
						gender: newVal.gender
					});

					if (result.isSuccess) {
						toast.success('Employee details updated');
						this.$refs.personalDetailsForm.toggleEdit(false);
						this.form.personalDetails = newVal;
					}
				} else {
					this.$refs.personalDetailsForm.toggleEdit(false);
					this.form.personalDetails = newVal;
				}
			},
			onPersonalErrorStateChanged(newVal) {
				this.personalFormHasError = newVal;
			},
			onResidentialAddressUpdated(newVal) {
				this.$refs.residentialAddressForm.toggleEdit(false);
				this.form.residentialAddress = newVal;
			},
			onResidentialAddressErrorStateChanged(newVal) {
				this.addressFormHasError = newVal;
			},
			async onEmergencyUpdated(newVal) {
				if (this.persistChanges) {
					const result = await this.$store.dispatch('employees/updateEmergency', {
						...newVal,
						employeeId: this.employeeId
					});

					if (result.isSuccess) {
						toast.success('Employee details updated');
						this.$refs.emergencyContactForm.toggleEdit(false);
						this.form.emergency = newVal;
					}
				} else {
					this.$refs.emergencyContactForm.toggleEdit(false);
					this.form.emergency = newVal;
				}
			},
			onEmergencyErrorStateChanged(newVal) {
				this.emergencyFormHasError = newVal;
			},
			async onTagsUpdated(newVal) {
				if (this.persistChanges) {
					const result = await this.$store.dispatch('employees/updateTags', {
						tags: newVal,
						employeeId: this.employeeId
					});

					if (result.isSuccess) {
						toast.success('Employee tags updated');
						this.$refs.tagsForm.toggleEdit(false);
						this.form.tags = newVal;
					}
				} else {
					this.$refs.tagsForm.toggleEdit(false);
					this.form.tags = newVal;
				}
			},
			async onHrPoliciesUpdated(newVal) {
				if (this.persistChanges) {
					const result = await this.$store.dispatch('employees/updateHrPolicies', {
						hrPolicies: newVal,
						employeeId: this.employeeId
					});

					if (result.isSuccess) {
						toast.success('HR Policies updated');
						this.$refs.hrPoliciesForm.toggleEdit(false);
						this.form.hrPolicies = newVal;
					}
				} else {
					this.$refs.hrPoliciesForm.toggleEdit(false);
					this.form.hrPolicies = newVal;
				}
			},
			async onOtherDetailsUpdated(newVal) {
				if (this.persistChanges) {
					const result = await this.$store.dispatch('employees/updateOther', {
						...newVal,
						employeeId: this.employeeId
					});

					if (result.isSuccess) {
						toast.success('Employee details updated');
						this.$refs.otherDetailsForm.toggleEdit(false);
						this.form.otherDetails = newVal;
					}
				} else {
					this.$refs.otherDetailsForm.toggleEdit(false);
					this.form.otherDetails = newVal;
				}
			},
			onUserAccountUpdated(newVal) {
				if (newVal == null) this.form.login.userId = null;
				else this.form.login.userId = newVal.userId;
			}
		}
	};
</script>

<style scoped>
	.scrollable-container {
		overflow-y: auto;
		max-height: calc(100vh - 300px);
	}
	/* custom scrollbar */
	.scrollable-container::-webkit-scrollbar {
		width: 20px;
	}

	.scrollable-container::-webkit-scrollbar-track {
		background-color: transparent;
	}

	.scrollable-container::-webkit-scrollbar-thumb {
		background-color: #d6dee1;
		border-radius: 20px;
		border: 6px solid transparent;
		background-clip: content-box;
	}

	.scrollable-container::-webkit-scrollbar-thumb:hover {
		background-color: #a8bbbf;
	}

	.employee-form .flex {
		padding-left: 8px;
		padding-right: 8px;
	}

	.employee-form .v-list div.has-error {
		background-color: #fbefef;
	}
</style>
